import React, { useEffect, useRef, useState } from 'react';
import { Space } from '@components/atoms/Space';
import { useTranslation } from 'react-i18next';
import styles from './LoginForm.module.css';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography } from '@components/atoms/Typography';
import { useData } from '@hooks/useData';
import { useInitLanguage } from '@hooks/useInitLanguage';
import { REGEX_IS_EMAIL, SITE_URLS } from '@constants';
import { useNavigate } from 'react-router-dom';
import { useLogin } from '@services/useLogin';
import { useGetRoles } from '@services/useUserTypes';
import { Form } from '@components/atoms/Form';
import { CredentialResponse, GoogleLogin } from '@react-oauth/google';
import { useGoogleAuthService } from '@services/useGoogleAuthService';
import { useNotifications } from '@hooks/useNotifications';
import { OTPInput } from '@components/atoms/OTPInput/OTPInput';
import { useGoogle2FAVerify } from '@services/useGoogle2FAVerify';
import { useUserLocation } from '@hooks/useUserLocation';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';

interface LoginFormErrors {
    login: string[];
    password: string[];
}

export const LoginForm: React.FC = () => {
    const { t } = useTranslation();
    const { showDefaultNotification } = useNotifications();
    const navigate = useNavigate();

    const { login: loginData, register, allSettings, language } = useData();
    useUserLocation(allSettings);

    useInitLanguage();

    const { request: requestLogin, error: errorLogin, lastResponseTime } = useLogin();
    const { request: requestRoles } = useGetRoles();
    const { handleGoogleLogin, error: googleLoginError, lastResponseTime: googleLastResponse } = useGoogleAuthService();
    const {
        request: requestVerify,
        loading: verifyLoading,
        error: verifyError,
        success: verifySuccess,
        lastResponseTime: verifyResponseTime,
    } = useGoogle2FAVerify();

    const formRef = useRef<HTMLFormElement>(null);
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [showPasswordLogin, setShowPasswordLogin] = useState(false);
    const [require2FA, setRequire2FA] = useState(false);

    const [loginFailed, setLoginFailed] = useState(false);

    const translations = {
        login: t('login.login'),
        register: t('login.register'),
        google: t('login.google'),
        email: t('login.continue'),
        loginByEmailOrPhone: t('login.email.login'),
        or: t('login.or'),
        no_account: t('login.no_account'),
        emailRequiredError: t('login.email.required-error'),
        emailInvalidError: t('login.email.invalid-error'),
        passwordRequiredError: t('login.password.required-error'),
        passwordIncorrectError: t('login.password.incorrect-error'),
        enterCode: t('2fa.enter-code'),
        verify: t('2fa.verify'),
        error: t('comon.error-occured'),
        forgot: t('login.password.forgot'),
    };

    const [errors, setErrors] = useState<LoginFormErrors>({
        login: [],
        password: [],
    });

    const validateForm = (): LoginFormErrors => {
        const newErrors: LoginFormErrors = { login: [], password: [] };
        if (!login.trim()) newErrors.login.push(translations.emailRequiredError);
        else if (!REGEX_IS_EMAIL.test(login.trim()) && !/^\+?[0-9\s\-]{7,15}$/.test(login.trim()))
            newErrors.login.push(translations.emailInvalidError);
        if (!password.trim()) newErrors.password.push(translations.passwordRequiredError);
        setErrors(newErrors);
        return newErrors;
    };

    const handleEmailSubmit = () => {
        const errs = validateForm();
        if (errs.login.length === 0) {
            setShowPasswordLogin(true);
            setErrors({ login: [], password: [] });
        }
    };

    const handleLoginSubmit = () => {
        const errs = validateForm();
        if (errs.login.length === 0 && errs.password.length === 0) {
            requestLogin({ login, password });
        }
    };

    const handleLoginSuccess = (response: CredentialResponse) => {
        handleGoogleLogin(response.credential ?? '');
    };

    const handleGoogleError = () => {
        if (googleLoginError) showDefaultNotification({ type: 'error', message: googleLoginError });
    };

    const handleVerify2FA = () => {
        const verifyEmail = loginData?.data?.email;
        if (typeof verifyEmail !== 'string') return;

        requestVerify({ email: verifyEmail, secret: otpCode });
    };

    const goToRegisterPage = () => navigate(`/${SITE_URLS.selectAccountType}`);

    const goToRecoverPassword = (e?: React.MouseEvent) => {
        e?.preventDefault();
        navigate(`/${SITE_URLS.recoverPassword}`, {
            state: { login },
        });
    };

    // login
    useEffect(() => {
        if (googleLoginError) {
            showDefaultNotification({ type: 'error', message: googleLoginError });
        } else if (register) {
            goToRegisterPage();
        } else if (loginData?.data?.redirect) {
            setRequire2FA(true);
        } else if (loginData?.success) {
            setLoginFailed(false);
            navigate(`/${SITE_URLS.map}`);
        }

        if (lastResponseTime && !loginData?.success) {
            setLoginFailed(true);
            showDefaultNotification({ type: 'error', message: errorLogin ?? translations.error });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [googleLastResponse, lastResponseTime]);

    useEffect(() => {
        requestRoles();
    }, []); // eslint-disable-line

    useEffect(() => {
        if (!verifyResponseTime) return;

        if (verifySuccess) {
            showDefaultNotification({ type: 'success', message: verifySuccess });
            navigate(`/${SITE_URLS.map}`);
        } else if (verifyError) {
            showDefaultNotification({ type: 'error', message: verifyError });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verifyResponseTime]);

    const handleOnEnterPress = () => (showPasswordLogin ? handleLoginSubmit() : handleEmailSubmit());

    return (
        <Form formRef={formRef} classes="d-flex flex-column w-100" onEnterPress={handleOnEnterPress} onSubmit={() => {}}>
            {!require2FA ? (
                <>
                    {!showPasswordLogin && (
                        <div className="d-flex flex-column justify-content-center">
                            <GoogleLogin onSuccess={handleLoginSuccess} onError={handleGoogleError} locale={language} />
                            <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                            <div className={styles.divider}>
                                <Typography fontSize="12">{translations.or}</Typography>
                            </div>
                            <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                        </div>
                    )}

                    <Input
                        type={InputTypes.text}
                        label={translations.loginByEmailOrPhone}
                        value={login}
                        onChange={(value) => setLogin(value)}
                        variant={InputVariants.labeledInput}
                        state={errors.login.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.login}
                        data-testid="login-input"
                    />

                    {showPasswordLogin && (
                        <>
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            <Input
                                type={InputTypes.password}
                                label="Password*"
                                variant={InputVariants.labeledInput}
                                value={password}
                                onChange={(value) => setPassword(value)}
                                state={errors.password.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.password}
                                data-testid="password-input"
                            />

                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                            <Anchor onClick={goToRecoverPassword} mode={AnchorMode.none}>
                                <Typography fontSize="16" fontWeight="300">
                                    {` ${translations.forgot}`}
                                </Typography>
                            </Anchor>

                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            <Button label={translations.login} mode={ButtonMode.primary} onClick={handleLoginSubmit} />
                        </>
                    )}

                    {!showPasswordLogin && (
                        <>
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            <Button label={translations.login} mode={ButtonMode.primary} onClick={handleEmailSubmit} />
                        </>
                    )}

                    <Typography classes="d-grid mb-0 mt-3 px-3 text-center text-nowrap" fontSize="12">
                        {translations.no_account}
                    </Typography>

                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    <Button label={translations.register} mode={ButtonMode.primary} onClick={goToRegisterPage} />
                </>
            ) : (
                // 2FA verify step
                <div className="d-flex flex-column align-items-center">
                    <Typography fontSize="16" fontWeight="800">
                        {translations.enterCode}
                    </Typography>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    <OTPInput value={otpCode} onChange={(val) => setOtpCode(val)} />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    <Button
                        label={translations.verify}
                        mode={ButtonMode.primary}
                        onClick={handleVerify2FA}
                        disabled={otpCode.length < 6 || verifyLoading}
                    />
                </div>
            )}
        </Form>
    );
};
